import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { OptionType, Select } from '@liveauctioneers/hammer-ui-core/select';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useNavigateSearchPageWithParams } from '@/pages/SearchPageSimplified/hooks/useNavigateSearchPageWithParams';
import { CurrentOpenMenu } from '@/pages/SearchPageSimplified/types';
import { useAppSelector } from '@/redux/hooks';
import { getSearchUiState } from '@/redux/modules/search/search.selectors';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import { FilterAction } from '@/types/SearchFacet';
import { modifyParamsRemovePage, modifyParamsWithSort, parseQueryParams } from '@/utils/queryParams';

type Props = {
    defaultSort?: SearchSortAndDirection;
    isAllFilters?: boolean;
    permittedSortOptions: SearchSortAndDirection[];
};

export const SortFilter = ({ defaultSort, isAllFilters, permittedSortOptions }: Props) => {
    const { search } = useLocation();
    const { formatMessage } = useIntl();

    const queryParams = parseQueryParams(search);

    const navigateWithParams = useNavigateSearchPageWithParams();
    const { sort: sortOptions } = useAppSelector(getSearchUiState);

    const onSortSelected = (sortOption: OptionType) => {
        let newParams = modifyParamsWithSort(queryParams, sortOption.value);
        newParams = modifyParamsRemovePage(newParams);
        navigateWithParams({
            checkForPageRemoval: true,
            currentOpenMenu: isAllFilters ? CurrentOpenMenu.AllFilters : undefined,
            filterAction: FilterAction.SortByChanged,
            params: newParams,
            preventScrollReset: true,
        });
    };

    const formattedOptions = useMemo(() => {
        const availableSortOptions = permittedSortOptions.length
            ? permittedSortOptions.filter((sort) => sortOptions.includes(sort))
            : sortOptions;

        return availableSortOptions.map((sort) => {
            const message = formatMessage({ id: `sorts.${sort}` });

            return {
                label: message,
                value: sort,
            };
        });
    }, [formatMessage, sortOptions, permittedSortOptions]);

    return (
        <Select
            className={cn('min-w-fit', {
                'mb-md': isAllFilters,
            })}
            data-testid="search-sort"
            instanceId="search-sort"
            isInsideLabel
            labelText={<FormattedMessage id="search_sort" />}
            name="sort"
            onChange={onSortSelected}
            options={formattedOptions}
            small
            value={
                formattedOptions.find((op) => op.value === queryParams.sort) ?? {
                    label: formatMessage({ id: `sorts.${defaultSort ?? SearchSortAndDirection.MostRelevant}` }),
                    value: defaultSort ?? SearchSortAndDirection.MostRelevant,
                }
            }
        />
    );
};
